import React, { Fragment, useMemo } from 'react'
import { css, cx } from '@emotion/css'
import { Tag } from '../../legacy-design-system/tags'
import { PresenceIndicator } from '../presence-indicator'
import { LoadingTextMask } from '../../legacy-design-system/loading-masks'
import { WithTooltip, TooltipLabel } from '../../design-system'
import { getServerDrilldownRoute } from '../route'
import { OverflowDetector } from '../../legacy-design-system/overflow-detector'
import { FormattedMessage } from 'react-intl'
import { theme as deprecatedTheme } from '../../styled/theme'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { CellRenderer } from '../../legacy-design-system/grid'
import { SyslogIndicator } from './syslog-indicator'
import { AgentStatusTooltip } from './agent-status-tooltip'

const MAX_TAGS = 20

const serverMetadataCellCss = css`
  padding-left: 8px;
  padding-right: 16px;
  width: 100%;
  overflow: hidden;
`

const serverMetadataTopRow = css`
  display: flex;
  align-items: center;
  max-width: 100%;
  overflow: hidden;
`

const presenceContainerCss = css`
  flex-grow: 0;
  margin-left: 8px;
`

const licensedContainerCss = css`
  flex-grow: 0;
  margin-left: 10px;
`

const agentVersionContainerCss = css`
  flex-grow: 1;
  margin-top: 1px;
  color: ${theme.color.neutral60};
  display: flex;
  font-size: 12px;
`

const serverNameContainerCss = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const serverNameAndPresenceCss = (hasTags: boolean) => css`
  display: flex;
  align-items: center;
  ${hasTags
    ? 'max-width: calc(100% - 27px);' // save room for the tags to be visible
    : 'min-width: 100%;'};
`

const syslogContainerCss = css`
  flex-grow: 0;
  margin-left: 8px;
`

const noWrapSpaceCss = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const tagsContainerCss = css`
  display: flex;
  margin-left: 4px;
  overflow: hidden;
`

const unlicensedCss = css`
  font-weight: ${deprecatedTheme.fontWeights.primary.regular};
  font-family: ${deprecatedTheme.fontFamilies.primaryCondensed};
  text-transform: uppercase;
  color: ${theme.color.neutral60};
  font-size: 12px;
`

export interface GetRendererProps {
  organizationID: string
  protect: boolean
  isFreemiumOrganization: boolean
}

export interface RowData {
  id: string
  name: string
  tags: string[]
  license?: {}
  online: boolean
  logLevel: string
  lastActivity: string
  agentVersion: string
  path: string
  hostname: string
  latestAgentVersion?: string
  outOfDate: boolean
  language: string | null
  syslogConfig: {
    enabled: boolean
    ipAddress?: string
  }
}

export function getServerMetadataCellRenderer({
  organizationID,
  protect,
  isFreemiumOrganization
}: GetRendererProps): CellRenderer<RowData> {
  return function serverMetadataCellRenderer({
    rowData: {
      id,
      name,
      tags,
      license,
      online,
      logLevel,
      path,
      hostname,
      lastActivity,
      agentVersion,
      syslogConfig,
      latestAgentVersion,
      outOfDate
    },
    getCellContentProps
  }) {
    const presenceContainerProps = getCellContentProps({
      className: presenceContainerCss
    })

    const syslogContainerProps = getCellContentProps({
      className: syslogContainerCss
    })

    const agentVersionContentProps = getCellContentProps({
      className: agentVersionContainerCss
    })

    const licensedContainerProps = getCellContentProps({
      className: licensedContainerCss
    })

    const tagsContainerProps = getCellContentProps({
      className: tagsContainerCss
    })

    const serverNameProps = getCellContentProps({
      className: serverNameContainerCss
    })

    return (
      <div className={serverMetadataCellCss}>
        <div className={serverMetadataTopRow}>
          <div className={serverNameAndPresenceCss(tags.length > 0)}>
            <div {...serverNameProps}>
              <ServerNameWithTooltip
                organizationID={organizationID}
                name={name}
                id={id}
                path={path}
                logLevel={logLevel}
                hostname={hostname}
              />
            </div>

            <div {...presenceContainerProps}>
              <PresenceIndicator
                online={online}
                lastActivity={Number.parseInt(lastActivity, 10)}
              />
            </div>
            {protect &&
              !isFreemiumOrganization &&
              syslogConfig.enabled &&
              syslogConfig.ipAddress && (
                <div {...syslogContainerProps}>
                  <SyslogIndicator ipAddress={syslogConfig.ipAddress} />
                </div>
              )}

            {protect && !license && !isFreemiumOrganization && (
              <div {...licensedContainerProps}>
                <FormattedMessage
                  id="title_servers_serverStatusUnlicensed"
                  defaultMessage="Unlicensed"
                >
                  {(unlicensedLabel) => {
                    return (
                      <span className={unlicensedCss}>{unlicensedLabel}</span>
                    )
                  }}
                </FormattedMessage>
              </div>
            )}
          </div>

          {tags.length > 0 && (
            <div {...tagsContainerProps}>
              {[...tags]
                .slice(0, MAX_TAGS) // stop scale issues, pop the modify dialog to see the full list
                .sort()
                .map((t, i) => (
                  <Tag key={i}>{t}</Tag>
                ))}
              {tags.length > MAX_TAGS ? (
                <Tag>{`+${tags.length - MAX_TAGS}`}</Tag>
              ) : null}
            </div>
          )}
        </div>
        <div {...agentVersionContentProps}>
          {agentVersion}
          {latestAgentVersion && (
            <span
              className={css`
                padding-left: 15px;
              `}
            >
              <AgentStatusTooltip
                latestAgentVersion={latestAgentVersion}
                outOfDate={outOfDate}
                placement="right-start"
              />
            </span>
          )}
        </div>
      </div>
    )
  }
}

const serverMetadataLoadingCellCss = cx(
  serverMetadataCellCss,
  css`
    width: 100%;
  `
)

const serverContainerLoadingCss = css`
  flex-grow: 1;
  width: 100%;
`

const serverNameContainerLoadingCss = cx(
  serverMetadataTopRow,
  css`
    display: flex;
    flex-direction: column;
    width: 60%;
  `
)

const maskedServerNameCss = css`
  width: 100%;
  padding: 1px 0px;
`

const maskedServerLanguageCss = cx(
  maskedServerNameCss,
  css`
    font-size: 0.6em;
  `
)

export function loadingServerMetadataCell() {
  return (
    <div className={serverMetadataLoadingCellCss}>
      <div className={serverContainerLoadingCss}>
        <div className={serverNameContainerLoadingCss}>
          <div className={maskedServerNameCss}>
            <LoadingTextMask min={7} />
          </div>
          <div className={maskedServerLanguageCss}>
            <LoadingTextMask min={3} max={4} />
          </div>
        </div>
      </div>
    </div>
  )
}

const serverNameTargetContainerCss = css`
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  line-height: 1.2em;
  white-space: nowrap;
`

const serverNameTextCss = cx(
  noWrapSpaceCss,
  css`
    color: ${theme.color.neutral100};
    cursor: pointer;

    &:hover {
      color: ${theme.color.neutral100};
      text-decoration: underline;
    }
  `
)

interface ServerNameWithTooltipProps {
  name: string
  path: string
  logLevel: string
  hostname: string
  organizationID: string
  id: string
}

function ServerNameWithTooltip({
  name,
  id,
  organizationID,
  path,
  hostname,
  logLevel
}: ServerNameWithTooltipProps) {
  const isNorthstarUI = useMemo(() => {
    return window.isEmbeddedInNS === true
  }, [])

  return (
    <OverflowDetector>
      {({ doesOverflowParent, registerElement }) => {
        return (
          <WithTooltip
            renderTooltipContent={() => (
              <Fragment>
                {doesOverflowParent() && (
                  <FormattedMessage id="title_name">
                    {(label) => {
                      return (
                        <TooltipLabel
                          label={label as any}
                          renderValue={() => name}
                        />
                      )
                    }}
                  </FormattedMessage>
                )}
                <FormattedMessage id="title_filesDirectory">
                  {(label) => {
                    return (
                      <TooltipLabel
                        label={label as any}
                        renderValue={() => path}
                      />
                    )
                  }}
                </FormattedMessage>
                <FormattedMessage id="title_hostname">
                  {(label) => {
                    return (
                      <TooltipLabel
                        label={label as any}
                        renderValue={() => hostname}
                      />
                    )
                  }}
                </FormattedMessage>
                <FormattedMessage id="title_logLevel">
                  {(label) => {
                    return (
                      <TooltipLabel
                        label={label as any}
                        renderValue={() => logLevel}
                      />
                    )
                  }}
                </FormattedMessage>
              </Fragment>
            )}
            placement="bottom-start"
          >
            {({ ref, onMouseEnter, onMouseLeave }) => (
              <span
                ref={ref}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                className={serverNameTargetContainerCss}
              >
                {isNorthstarUI ? (
                  <span
                    data-e2e={`server-name-cell-${name}`}
                    ref={registerElement}
                  >
                    {name}
                  </span>
                ) : (
                  <a
                    data-e2e={`server-name-cell-${name}`}
                    ref={registerElement}
                    className={serverNameTextCss}
                    href={getServerDrilldownRoute(organizationID, id)}
                  >
                    {name}
                  </a>
                )}
              </span>
            )}
          </WithTooltip>
        )
      }}
    </OverflowDetector>
  )
}
